import { Department } from "../../../../domain/department";
import {
  ExternalScriptExecuteResult,
  CellInformation,
  ExecuteRequest,
  ScriptLinkItem,
  BatchRequest,
  BatchResult,
} from "../../../../domain/localhost/items";
import {
  IExternalDataResultBean,
  IObjectInformationBean,
  IExternalDataRequestBean,
  IScriptLinkItemBean,
  IBatchResultsBean,
} from "../beans";
import { IBatchRequestBean } from "../beans/IBatchRequestBean";
import { IBatchRequestsBean } from "../beans/IBatchRequestsBean";
import { IBatchResultBean } from "../beans/IBatchResultBean";

export class ExecuteResultTranslator {
  public static toExternalScriptExecuteResult(bean: IExternalDataResultBean): ExternalScriptExecuteResult {
    if (!bean) { return null; }
    const result = new ExternalScriptExecuteResult();
    result.isComplete = bean.isComplete;
    result.isCancel = bean.isCancel;
    result.values = this.toResultValues(bean.values);
    result.compressedCsvText = bean.compressedCsvText;
    result.scriptLinkResults = this.toScriptLinkItems(bean.scriptLinkResults);
    return result;
  }

  private static toResultValues(beans: IObjectInformationBean[]): CellInformation[] {
    if (!beans) { return []; }
    return beans.map(bean => this.toResultValue(bean));
  }

  private static toResultValue(bean: IObjectInformationBean): CellInformation {
    const value = new CellInformation();
    value.row = bean.row;
    value.column = bean.column;
    value.value = bean.value;
    return value;
  }

  private static toScriptLinkItems(beans: IScriptLinkItemBean[]): ScriptLinkItem[] {
    if (!beans) { return []; }
    const results: ScriptLinkItem[] = [];
    for (const bean of beans) {
      if (bean.action !== "input" && bean.action !== "select") { continue; }
      // 結果はinputかselectで返ってくる
      results.push(this.toScriptLinkItem(bean));
    }
    return results;
  }

  private static toScriptLinkItem(bean: IScriptLinkItemBean): ScriptLinkItem {
    const item = new ScriptLinkItem();
    item.name = bean.name;
    item.subName = bean.subName;
    item.itemName = bean.itemName;
    item.objectId = bean.objectId;
    item.value = bean.value;
    item.claioImageId = bean.claioImageId;
    item.cacheImageGuid = bean.cacheImageGuid;
    item.externalImageId = bean.externalImageId;
    return item;
  }

  public static toExternalDataRequestBean(request: any): IExternalDataRequestBean {
    if (!request) { return null; }
    if (request instanceof ExecuteRequest) {
      return {
        patientId: request.patientId,
        exePath: request.exePath,
        csvPath: request.csvPath,
        xmlValuePath: request.xmlValuePath,
        csvBeforeValuePath: request.csvBeforeValuePath,
        csvInputValuePath: request.csvInputValuePath,
        arguments: request.arguments,
        splitter: request.splitter,
        values: request.values,
        scriptLinkItems: request.scriptLinkItems.map(i => this.toScriptLinkItemBean(i)),
        previousCompressedCsvText: request.previousCompressedCsvText,
        departmentCode: request.department ? request.department.id : null,
        departmentName: request.department ? request.department.name : null,
      };
    }
    throw new Error("not implemented");
  }

  private static toScriptLinkItemBean(item: ScriptLinkItem): IScriptLinkItemBean {
    return {
      action: "value",  // 値はvalueで提供する
      name: item.name,
      subName: item.subName,
      objectId: item.objectId,
      itemName: item.itemName,
      value: item.value,
      claioImageId: item.claioImageId,
      cacheImageGuid: null, // TODO: 画像データを外部処理に渡す処理がある場合は実装が必要
      externalImageId: item.externalImageId,
    };
  }

  public static toBatchRequestsBean(requests: BatchRequest[]): IBatchRequestsBean {
    return {
      requests: this.toBatchRequestBeans(requests),
    };
  }
  private static toBatchRequestBeans(requests: BatchRequest[]): IBatchRequestBean[] {
    if (!requests) { return []; }
    return requests.map(r => this.toBatchRequestBean(r));
  }

  private static toBatchRequestBean(request: BatchRequest): IBatchRequestBean {
    return {
      action: request.action,
      request: this.toExternalDataRequestBean(request.request),
      id: request.source.privateId,
    };
  }

  public static toBatchResults(bean: IBatchResultsBean, requests: BatchRequest[]): BatchResult[] {
    const results: BatchResult[] = [];
    for (const request of requests) {
      const found = this.findBatchResult(bean.results, request.source.privateId);
      const result = new BatchResult();
      result.request = request;
      result.result = this.toExternalScriptExecuteResult(found.result);
      results.push(result);
    }
    return results;
  }

  private static findBatchResult(beans: IBatchResultBean[], id: number): IBatchResultBean {
    if (!beans) { return null; }
    for (const bean of beans) {
      if (bean.id === id) { return bean; }
    }
    return null;
  }

  public static toExternalDataRequest(bean: IExternalDataRequestBean): ExecuteRequest {
    if (!bean) { return null; }
    const request = new ExecuteRequest();
    request.patientId = bean.patientId;
    request.department = bean.departmentCode ? new Department(bean.departmentCode, bean.departmentName, "") : null;
    request.exePath = bean.exePath;
    request.csvPath = bean.csvPath;
    request.xmlValuePath = bean.xmlValuePath;
    request.csvBeforeValuePath = bean.csvBeforeValuePath;
    request.csvInputValuePath = bean.csvInputValuePath;
    request.arguments = bean.arguments;
    request.splitter = bean.splitter;
    request.values = bean.values;
    request.scriptLinkItems = bean.scriptLinkItems.map(i => this.toScriptLinkItem(i));  // actionはvalue固定のはず
    request.previousCompressedCsvText = bean.previousCompressedCsvText;
    return request;
  }

  public static toExternalScriptExecuteResultBean(result: ExternalScriptExecuteResult): IExternalDataResultBean {
    if (!result) { return null; }
    return {
      isComplete: result.isComplete,
      isCancel: result.isCancel,
      values: result.values ? result.values.map(v => this.toResultValueBean(v)) : [],
      compressedCsvText: result.compressedCsvText,
      scriptLinkResults: result.scriptLinkResults ? result.scriptLinkResults.map(i => this.toResultScriptLinkItemBean(i)) : [],
    };
  }

  private static toResultValueBean(cell: CellInformation): IObjectInformationBean {
    return {
      row: cell.row,
      column: cell.column,
      value: cell.value,
    };
  }

  private static toResultScriptLinkItemBean(item: ScriptLinkItem): IScriptLinkItemBean {
    return {
      action: "input",
      name: item.name,
      subName: item.subName,
      objectId: item.objectId,
      itemName: item.itemName,
      value: item.value,
      claioImageId: item.claioImageId,
      cacheImageGuid: item.cacheImageGuid,
      externalImageId: item.externalImageId,
    };
  }
}
